import { useEffect, useMemo, useState } from 'react'
import { Link } from '@inertiajs/react'
import { loadStripe, type Stripe, type Appearance } from '@stripe/stripe-js'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import Seo from '../components/Seo'
import { useShared } from '../lib/hooks'

const appearance: Appearance = {
  theme: 'night',
  variables: {
    colorPrimary: '#fafaf9',
    colorBackground: '#0f0f0f',
    colorText: '#fafaf9',
    colorTextSecondary: '#737373',
    fontFamily: 'Space Grotesk, system-ui, sans-serif',
    borderRadius: '4px',
  },
}

// Use the fresh XSRF-TOKEN cookie (Rails refreshes it every response, including
// after login's session rotation) rather than the <meta> token, which is only
// rendered on full page loads and goes stale across SPA navigation. The
// inertia_rails middleware copies X-XSRF-TOKEN → X-CSRF-Token server-side.
function csrfHeaders(): Record<string, string> {
  if (typeof document === 'undefined') return {}
  const m = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/)
  if (m) return { 'X-XSRF-TOKEN': decodeURIComponent(m[1]) }
  const meta = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content
  return meta ? { 'X-CSRF-Token': meta } : {}
}

export default function Checkout({ stripe_publishable_key }: { stripe_publishable_key: string }) {
  const { cart } = useShared()
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [returnUrl, setReturnUrl] = useState<string>('')
  const [error, setError] = useState<string | null>(null)

  const stripePromise = useMemo<Promise<Stripe | null> | null>(
    () => (stripe_publishable_key ? loadStripe(stripe_publishable_key) : null),
    [stripe_publishable_key],
  )

  useEffect(() => {
    let active = true
    fetch('/checkout/payment_intent', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...csrfHeaders() },
    })
      .then(async (r) => {
        // Guard against an HTML response (auth redirect / error page) so we never
        // try to JSON.parse "<!DOCTYPE …".
        const isJson = r.headers.get('content-type')?.includes('application/json')
        if (!isJson) {
          throw new Error(
            r.status === 401 || r.redirected
              ? 'Your session expired. Please refresh and sign in again.'
              : 'Could not start checkout. Please refresh and try again.',
          )
        }
        const data = await r.json()
        if (!r.ok) throw new Error(data.error || 'Could not start checkout.')
        // A discount can bring the total to (effectively) free — no Stripe step.
        if (data.free && data.redirect_url) {
          window.location.href = data.redirect_url
          return
        }
        if (active) {
          setClientSecret(data.client_secret)
          setReturnUrl(data.return_url)
        }
      })
      .catch((e) => active && setError(e.message))
    return () => {
      active = false
    }
  }, [])

  return (
    <>
      <Seo title="Checkout" noindex />
      <div className="mx-auto max-w-5xl px-4 sm:px-6 py-12 grid lg:grid-cols-2 gap-10 lg:gap-16">
        {/* Summary */}
        <div className="lg:order-2">
          <h1 className="text-2xl font-semibold tracking-tight lg:hidden mb-6">Checkout</h1>
          <div className="border border-line p-5">
            <div className="mono-label mb-4">Order summary</div>
            <ul className="divide-y divide-line">
              {cart.items.map((item) => (
                <li key={item.id} className="flex items-center gap-3 py-3">
                  <div className="size-11 shrink-0 bg-ink-soft border border-line overflow-hidden">
                    {item.image && <img src={item.image} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0 text-sm truncate">{item.name}</div>
                  <div className="tabular-nums text-sm">{item.price}</div>
                </li>
              ))}
            </ul>
            <div className="mt-4 pt-4 border-t border-line space-y-2">
              <div className="flex items-center justify-between text-sm text-muted">
                <span className="mono-label">Subtotal</span>
                <span className="tabular-nums">{cart.subtotal}</span>
              </div>
              {cart.discount && (
                <div className="flex items-center justify-between text-sm text-emerald-400">
                  <span className="mono-label !text-emerald-400">
                    Discount{cart.discount.code ? ` · ${cart.discount.code}` : ''}
                  </span>
                  <span className="tabular-nums">−{cart.discount.amount}</span>
                </div>
              )}
              <div className="flex items-center justify-between pt-1">
                <span className="mono-label">Total</span>
                <span className="text-xl font-semibold tabular-nums">{cart.total}</span>
              </div>
            </div>
          </div>
          <Link href="/cart" className="mono-label inline-block mt-4 hover:text-paper transition-colors">← Edit cart</Link>
        </div>

        {/* Payment */}
        <div className="lg:order-1">
          <h1 className="text-2xl font-semibold tracking-tight hidden lg:block mb-6">Checkout</h1>

          {error && (
            <div className="rounded-md border border-red-500/40 bg-red-500/10 text-red-300 px-4 py-3 text-sm mb-4">
              {error}
            </div>
          )}

          {!clientSecret && !error && (
            <div className="text-muted text-sm py-10">Preparing secure payment…</div>
          )}

          {clientSecret && stripePromise && (
            <Elements stripe={stripePromise} options={{ clientSecret, appearance }}>
              <PaymentForm returnUrl={returnUrl} total={cart.total} />
            </Elements>
          )}
        </div>
      </div>
    </>
  )
}

function PaymentForm({ returnUrl, total }: { returnUrl: string; total: string }) {
  const stripe = useStripe()
  const elements = useElements()
  const [submitting, setSubmitting] = useState(false)
  const [message, setMessage] = useState<string | null>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!stripe || !elements) return
    setSubmitting(true)
    setMessage(null)
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
    })
    if (error) {
      setMessage(error.message ?? 'Payment failed. Please try again.')
      setSubmitting(false)
    }
    // On success Stripe redirects to return_url.
  }

  return (
    <form onSubmit={submit}>
      <PaymentElement />
      {message && <div className="mt-3 text-sm text-red-400">{message}</div>}
      <button
        type="submit"
        disabled={!stripe || submitting}
        className="w-full mt-6 h-12 bg-paper text-ink text-sm font-semibold hover:opacity-90 transition disabled:opacity-60"
      >
        {submitting ? 'Processing…' : `Pay ${total}`}
      </button>
    </form>
  )
}
